import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getConfig } from '../configs/config'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { loginRequest } from '../authConfig'
import { fetchEventSource, EventSourceMessage } from '@microsoft/fetch-event-source'
import { isValidJSON } from '../views/_pages/Chatbot/smart-flow/utils'

export interface StreamDataProps {
  url: string
  data: Record<string, unknown> | any
  hasFiles?: boolean
  abortSignal?: AbortSignal
  onmessage: (event: EventSourceMessage) => void
  method?: 'post' | 'put'
}

export const useStream = (app = 'inv') => {
  const { accounts, instance } = useMsal()
  const config = getConfig()
  const [isLoading, setLoader] = useState(false)
  const [error, setError] = useState<{ message: string } | null>(null)

  const request = {
    ...loginRequest,
    scopes: [app === 'nexus' ? config.REACT_NEXUS_AZURE_SCOPE : config.REACT_APP_AZURE_SCOPE],
    account: accounts[0]
  }

  const streamData = async ({
    url,
    data = {},
    hasFiles = false,
    abortSignal,
    onmessage,
    method = 'post'
  }: StreamDataProps) => {
    setLoader(true)
    const msalResponse = await instance.acquireTokenSilent(request).catch((e: any) => {
      if (e instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(request)
      }
    })

    const options = {
      method,
      headers: {
        Accept: '*/*',
        ...(!hasFiles && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${msalResponse?.accessToken}`
      }
    }

    try {
      let endpoint = url
      if (!endpoint.startsWith('https')) {
        const baseUrl = app === 'nexus' ? config.REACT_NEXUS_API_URL : config.REACT_APP_API_URL
        endpoint = `${baseUrl}${url}`
      }

      await fetchEventSource(endpoint, {
        ...options,
        ...(abortSignal && { signal: abortSignal }),
        body: hasFiles ? data : JSON.stringify(data),
        openWhenHidden: true, // Allow the stream to open even if the tab is hidden or inactive
        onopen: async (response) => {
          if (!response.ok) {
            // Not a stream, handle error body
            const errorText = await response.text()
            setError({ message: errorText || `Error: ${response.status}` })
            setLoader(false)
            // Throw to stop further processing
            throw new Error(errorText || `Error: ${response.status}`)
          }
        },
        onmessage,
        onclose: () => {
          // Normal stream closure - not an error
          setLoader(false)
        },
        onerror: (err) => {
          // Check if it's an abort (user stopped) vs actual error
          if (err.name === 'AbortError' || abortSignal?.aborted) {
            setError({ message: 'Request aborted' })
            setLoader(false)
            throw err
          }
          // Other errors - could be network issues
          setError({ message: err.message })
          setLoader(false)
          throw err
        }
      })
    } catch (error: any) {
      // Only return error for actual errors, not normal stream completion or user abort
      if (error?.name === 'AbortError' || abortSignal?.aborted) {
        return { message: 'Request aborted', isAborted: true }
      }

      const errorMessage: any = isValidJSON(error?.message)
        ? JSON.parse(error?.message)
        : { message: error?.message || 'Unknown error' }
      setError({ message: errorMessage?.message })
      console.error('Stream error:', error)
      return errorMessage
    } finally {
      setLoader(false)
    }
  }

  return [isLoading, streamData, error] as const
}
